import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, X, ExternalLink, Eye, ThumbsUp, MessageCircle,
  Youtube, Users, Play, TrendingUp, BarChart2, Video, Info,
  Calendar, Clock, Zap, Target, Hash
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, ScatterChart, Scatter
} from 'recharts';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const fmt = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000000) return (n / 1000000).toFixed(2) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
};

const fmtShort = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function InfoTip({ text }) {
  return (
    <span className="relative group/tip inline-flex ml-1">
      <Info className="w-3 h-3 text-salty-text-muted hover:text-red-400 cursor-help transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-salty-bg text-salty-text-main text-[11px] leading-relaxed font-normal normal-case tracking-normal px-3 py-2.5 rounded-lg border border-salty-border shadow-xl opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 z-50">
        {text}
      </span>
    </span>
  );
}

function StatCard({ title, value, desc, color, info, icon }) {
  const colorMap = {
    red: 'text-red-400',
    emerald: 'text-emerald-400',
    purple: 'text-salty-purple',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    pink: 'text-pink-400',
  };
  return (
    <div className="bg-salty-card border border-salty-border rounded-2xl p-5 hover:border-red-400/20 transition-colors flex flex-col justify-between">
      <div className="flex items-center gap-1.5 text-xs text-salty-text-muted uppercase tracking-wider font-bold mb-2">
        {icon && <span className={colorMap[color]}>{icon}</span>}
        {title}
        {info && <InfoTip text={info} />}
      </div>
      <div className={`text-3xl font-black ${colorMap[color]} mb-1`}>{value}</div>
      <div className="text-xs text-salty-text-muted mt-1">{desc}</div>
    </div>
  );
}

export default function YouTubeTrackerModal({ influencer, onClose, onSyncComplete }) {
  const [loading, setLoading] = useState(true);
  const [channelData, setChannelData] = useState(null);
  const [recentVideos, setRecentVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handle = (influencer.youtube_handle || '').replace(/^@+/, '').trim();

  useEffect(() => {
    loadData();
  }, [influencer.id]);

  const loadData = async () => {
    setLoading(true);
    setChannelData({
      name: influencer.youtube_channel_name || influencer.name,
      handle,
      profile_pic: influencer.youtube_profile_pic,
      subscribers: influencer.youtube_subscribers || 0,
      avg_views: influencer.youtube_avg_views || 0,
      total_views: influencer.youtube_total_views || 0,
      video_count: influencer.youtube_video_count || 0,
      synced_at: influencer.youtube_synced_at,
    });
    setLoading(false);
    // Load recent videos in background
    if (influencer.youtube_channel_id) {
      fetchRecentVideos();
    }
  };

  const fetchRecentVideos = async () => {
    setVideosLoading(true);
    try {
      const res = await api.getYouTubeRecentVideos(influencer.id);
      setRecentVideos(res.data.videos || []);
    } catch (err) {
      // non-fatal
    } finally {
      setVideosLoading(false);
    }
  };

  const handleRefresh = async () => {
    setSyncing(true);
    try {
      const res = await api.syncYouTubeMetrics(influencer.id);
      const d = res.data;
      setChannelData({
        name: d.name,
        handle,
        profile_pic: d.profile_pic,
        subscribers: d.subscribers,
        avg_views: d.avg_views_per_video,
        total_views: d.total_views,
        video_count: d.video_count,
        synced_at: d.synced_at,
      });
      toast.success('YouTube data refreshed');
      if (onSyncComplete) onSyncComplete(d);
      // Also refresh recent videos
      fetchRecentVideos();
    } catch (err) {
      toast.error('Failed to refresh YouTube data');
    } finally {
      setSyncing(false);
    }
  };

  // Derived metrics
  const er = channelData && channelData.subscribers > 0
    ? ((channelData.avg_views / channelData.subscribers) * 100).toFixed(1)
    : '0';

  const videosPerWeek = channelData && channelData.video_count > 0
    ? (channelData.video_count / 52).toFixed(1)
    : '—';

  // Computed from recent videos
  const recentStats = useMemo(() => {
    if (!recentVideos.length) return null;
    const views = recentVideos.map(v => v.views || 0);
    const likes = recentVideos.map(v => v.likes || 0);
    const comments = recentVideos.map(v => v.comments || 0);
    const avgViews = Math.round(views.reduce((a, b) => a + b, 0) / views.length);
    const avgLikes = Math.round(likes.reduce((a, b) => a + b, 0) / likes.length);
    const avgComments = Math.round(comments.reduce((a, b) => a + b, 0) / comments.length);
    const topVideo = recentVideos.reduce((a, b) => (a.views || 0) > (b.views || 0) ? a : b, recentVideos[0]);
    const avgEr = channelData?.subscribers > 0
      ? (((avgLikes + avgComments) / channelData.subscribers) * 100).toFixed(2)
      : '0';
    const viralCount = recentVideos.filter(v => v.views > avgViews * 2).length;
    const viralRatio = Math.round((viralCount / recentVideos.length) * 100);
    return { avgViews, avgLikes, avgComments, topVideo, avgEr, viralRatio };
  }, [recentVideos, channelData]);

  // Chart data from recent videos (sorted by date)
  const chartData = useMemo(() => {
    if (!recentVideos.length) return [];
    return [...recentVideos]
      .filter(v => v.published_at)
      .sort((a, b) => new Date(a.published_at) - new Date(b.published_at))
      .map(v => ({
        date: fmtShort(v.published_at),
        views: v.views || 0,
        engagement: (v.likes || 0) + (v.comments || 0),
        likes: v.likes || 0,
        comments: v.comments || 0,
      }));
  }, [recentVideos]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-salty-card w-full max-w-6xl rounded-2xl border border-salty-border shadow-2xl flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3 text-salty-text-muted">
            <RefreshCw className="w-6 h-6 animate-spin text-red-400" />
            <p className="text-sm">Loading YouTube data…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-6">
      <div className="bg-salty-card w-full max-w-6xl max-h-[92vh] rounded-2xl border border-salty-border shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-salty-border shrink-0">
          <div className="flex items-center gap-3">
            <Youtube className="w-5 h-5 text-red-400" />
            <span className="font-bold text-salty-text-main text-lg">YouTube Analytics</span>
            {channelData?.synced_at && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-salty-text-muted border border-salty-border rounded px-2 py-0.5">
                Last synced: {timeAgo(channelData.synced_at)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-salty-border text-salty-text-main hover:bg-salty-subtle transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              Refresh Data
            </button>
            <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-salty-subtle text-salty-text-main hover:bg-salty-border transition-all">
              <X className="w-3.5 h-3.5" /> Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Layout: 4-col grid like Instagram */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* LEFT: Profile card */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-salty-card rounded-2xl border border-salty-border p-6 text-center">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-red-400/20 bg-salty-bg/80 flex items-center justify-center overflow-hidden">
                  {channelData?.profile_pic
                    ? <img src={channelData.profile_pic} alt={channelData.name} className="w-full h-full object-cover" />
                    : <Youtube className="w-10 h-10 text-red-400" />
                  }
                </div>
                <h2 className="text-xl font-bold text-salty-text-main">{channelData?.name}</h2>
                <a
                  href={`https://youtube.com/@${handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-400 hover:underline flex justify-center items-center gap-1 mb-4 text-sm"
                >
                  @{handle} <ExternalLink className="w-3 h-3" />
                </a>

                <div className="grid grid-cols-2 gap-3 text-left">
                  <div>
                    <div className="text-xs text-salty-text-muted uppercase tracking-wider">Subscribers</div>
                    <div className="text-xl font-bold text-salty-text-main">{fmt(channelData?.subscribers)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-salty-text-muted uppercase tracking-wider">Videos</div>
                    <div className="text-xl font-bold text-salty-text-main">{fmt(channelData?.video_count)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-salty-text-muted uppercase tracking-wider">Total Views</div>
                    <div className="text-xl font-bold text-salty-text-main">{fmt(channelData?.total_views)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-salty-text-muted uppercase tracking-wider">View Rate</div>
                    <div className="text-xl font-bold text-red-400">{er}%</div>
                  </div>
                </div>
              </div>

              {/* Quick channel stats */}
              <div className="bg-salty-card rounded-2xl border border-salty-border p-5">
                <h3 className="text-sm font-bold text-salty-text-muted uppercase tracking-wider mb-4">Channel Vitals</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Avg Views / Video', value: fmt(channelData?.avg_views), color: 'text-red-400' },
                    { label: 'Est. Videos / Week', value: videosPerWeek, color: 'text-amber-400' },
                    { label: 'Recent Avg Views', value: recentStats ? fmt(recentStats.avgViews) : '—', color: 'text-blue-400' },
                    { label: 'Recent Avg ER', value: recentStats ? `${recentStats.avgEr}%` : '—', color: 'text-emerald-400' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-xs text-salty-text-muted">{label}</span>
                      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT: Analytics */}
            <div className="lg:col-span-3 space-y-6">

              {/* KPI stat cards — 4 across */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  title="View Rate"
                  value={`${er}%`}
                  desc="Avg views as % of subscribers"
                  color={parseFloat(er) > 10 ? 'emerald' : parseFloat(er) > 3 ? 'amber' : 'red'}
                  icon={<TrendingUp className="w-3.5 h-3.5" />}
                  info="Average views per video divided by subscriber count × 100. Above 10% is excellent for YouTube."
                />
                <StatCard
                  title="Avg Reel Views"
                  value={recentStats ? fmt(recentStats.avgViews) : fmt(channelData?.avg_views)}
                  desc={recentStats ? `From last ${recentVideos.length} videos` : 'Channel-wide estimate'}
                  color="red"
                  icon={<Eye className="w-3.5 h-3.5" />}
                  info="Average views across the most recent videos fetched."
                />
                <StatCard
                  title="Avg Likes"
                  value={recentStats ? fmt(recentStats.avgLikes) : '—'}
                  desc="Per video (recent)"
                  color="pink"
                  icon={<ThumbsUp className="w-3.5 h-3.5" />}
                  info="Average like count from recent videos. YouTube hides likes for some creators."
                />
                <StatCard
                  title="Viral Ratio"
                  value={recentStats ? `${recentStats.viralRatio}%` : '—'}
                  desc="Videos > 2× avg views"
                  color="purple"
                  icon={<Zap className="w-3.5 h-3.5" />}
                  info="Percentage of recent videos that generated more than twice the average views — a measure of content upside potential."
                />
              </div>

              {/* Consistency panel */}
              <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
                <h3 className="text-base font-bold text-salty-text-main mb-4">Consistency & Output</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                    <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                      Videos / Week
                      <InfoTip text="Estimated based on total video count ÷ 52 weeks." />
                    </div>
                    <div className="text-2xl font-bold text-salty-text-main">{videosPerWeek}</div>
                  </div>
                  <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                    <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                      Total Videos
                      <InfoTip text="Total public videos on the channel." />
                    </div>
                    <div className="text-2xl font-bold text-salty-text-main">{fmt(channelData?.video_count)}</div>
                  </div>
                  <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                    <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                      Avg Comments
                      <InfoTip text="Average comment count from recent videos." />
                    </div>
                    <div className="text-2xl font-bold text-salty-text-main">{recentStats ? fmt(recentStats.avgComments) : '—'}</div>
                  </div>
                  <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                    <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                      Top Video Views
                      <InfoTip text="Views on the most-viewed video among the recent batch fetched." />
                    </div>
                    <div className="text-2xl font-bold text-red-400">{recentStats ? fmt(recentStats.topVideo?.views) : '—'}</div>
                  </div>
                </div>
              </div>

              {/* Charts */}
              {chartData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
                    <h3 className="text-base font-bold text-salty-text-main mb-4">Views Performance</h3>
                    <div className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={v => fmt(v)} />
                          <Tooltip
                            cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                            contentStyle={{ background: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: 8, fontSize: 12, color: 'var(--salty-text-main)' }} labelStyle={{ color: 'var(--salty-text-muted)', fontWeight: 700, marginBottom: 4 }}
                            formatter={(v) => [fmt(v), 'Views']}
                          />
                          <Bar dataKey="views" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
                    <h3 className="text-base font-bold text-salty-text-main mb-4">Engagement Trend</h3>
                    <div className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={v => fmt(v)} />
                          <Tooltip
                            contentStyle={{ background: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: 8, fontSize: 12, color: 'var(--salty-text-main)' }} labelStyle={{ color: 'var(--salty-text-muted)', fontWeight: 700, marginBottom: 4 }}
                            formatter={(v, name) => [fmt(v), name === 'engagement' ? 'Likes+Comments' : name]}
                          />
                          <Line type="monotone" dataKey="engagement" stroke="#34d399" strokeWidth={2.5} dot={{ fill: '#34d399', r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Campaign Video Tracker */}
              <VideoTracker influencer={influencer} />

              {/* Recent Videos Preview */}
              <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base font-bold text-salty-text-main">Recent Videos Preview</h3>
                  {videosLoading && <RefreshCw className="w-4 h-4 animate-spin text-salty-text-muted" />}
                </div>
                {recentVideos.length === 0 && !videosLoading ? (
                  <div className="py-10 text-center bg-salty-bg/60 rounded-xl border border-dashed border-salty-border">
                    <Youtube className="w-8 h-8 text-salty-border mx-auto mb-2" />
                    <p className="text-sm text-salty-text-muted">
                      {influencer.youtube_channel_id
                        ? 'No recent videos fetched — click Refresh Data'
                        : 'Sync the channel first to load recent videos'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {recentVideos.map((video) => {
                      const videoEr = channelData?.subscribers > 0
                        ? (((video.likes || 0) + (video.comments || 0)) / channelData.subscribers * 100).toFixed(2)
                        : null;
                      const avgEr = parseFloat(recentStats?.avgEr || 0);
                      const isAboveAvg = videoEr && parseFloat(videoEr) >= avgEr;

                      return (
                        <a
                          key={video.video_id}
                          href={`https://youtube.com/watch?v=${video.video_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block bg-salty-bg/80 rounded-xl border border-salty-border hover:border-red-400/40 transition-colors"
                        >
                          {/* Thumbnail */}
                          <div className="aspect-video bg-salty-border relative rounded-t-xl overflow-hidden">
                            {video.thumbnail ? (
                              <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Youtube className="w-8 h-8 text-salty-text-muted" />
                              </div>
                            )}
                            <div className="absolute top-2 right-2 bg-black/60 rounded p-1">
                              <Play className="w-3 h-3 text-white" />
                            </div>
                          </div>
                          {/* Info */}
                          <div className="p-3">
                            <div className="flex flex-wrap gap-2 items-center text-xs text-salty-text-muted mb-2 font-medium">
                              <div className="flex items-center gap-1" title="Views">
                                <Eye className="w-3 h-3 text-red-400" /> {fmt(video.views)}
                              </div>
                              <div className="flex items-center gap-1" title="Likes">
                                <ThumbsUp className="w-3 h-3 text-pink-400" /> {video.likes > 0 ? fmt(video.likes) : <span className="opacity-50">—</span>}
                              </div>
                              <div className="flex items-center gap-1" title="Comments">
                                <MessageCircle className="w-3 h-3 text-salty-text-muted" /> {fmt(video.comments)}
                              </div>
                            </div>
                            <p className="text-xs text-salty-text-main line-clamp-2 min-h-[28px]">{video.title}</p>
                            <div className="mt-2.5 pt-2.5 border-t border-salty-border flex items-center justify-between">
                              <span className="text-[10px] text-salty-text-muted">{fmtDate(video.published_at)}</span>
                              {videoEr && (
                                <span className={`text-xs font-bold ${isAboveAvg ? 'text-emerald-400' : 'text-amber-400'}`}>
                                  ER {videoEr}%
                                </span>
                              )}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Track individual YouTube video metrics — persists to DB
function VideoTracker({ influencer }) {
  const [url, setUrl] = useState('');
  const [tracked, setTracked] = useState(influencer.youtube_tracked_videos || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const persist = async (videos) => {
    setSaving(true);
    try {
      await api.updateYouTubeTrackedVideos(influencer.id, videos);
    } catch (err) {
      console.error('Failed to persist tracked videos', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTrack = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await api.fetchYouTubeVideo(url.trim());
      const v = res.data;
      setTracked(prev => {
        if (prev.find(p => p.video_id === v.video_id)) {
          toast.info('Video already tracked');
          return prev;
        }
        const next = [v, ...prev];
        persist(next);
        return next;
      });
      setUrl('');
      toast.success(`Tracked: ${v.title}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to fetch video metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (videoId) => {
    setTracked(prev => {
      const next = prev.filter(v => v.video_id !== videoId);
      persist(next);
      return next;
    });
  };

  const chartData = tracked.map(v => ({
    title: v.title.length > 18 ? v.title.slice(0, 18) + '…' : v.title,
    views: v.views,
    likes: v.likes,
    comments: v.comments,
  }));

  return (
    <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-salty-text-main flex items-center gap-2">
            <Target className="w-4 h-4 text-red-400" />
            Campaign Videos
            {saving && <RefreshCw className="w-3 h-3 animate-spin text-salty-text-muted" />}
          </h3>
          <p className="text-xs text-salty-text-muted mt-0.5">Track individual campaign videos and measure isolated performance</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleTrack()}
          placeholder="Paste YouTube or Shorts URL..."
          className="flex-1 bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-sm text-salty-text-main placeholder-salty-text-muted/50 focus:outline-none focus:border-red-400/50"
        />
        <button
          onClick={handleTrack}
          disabled={loading || !url.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
        >
          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Track Video
        </button>
      </div>

      {tracked.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 border border-dashed border-salty-border rounded-xl text-salty-text-muted">
          <Target className="w-8 h-8 mb-2 opacity-30" />
          <p className="text-sm">No campaign videos tracked yet</p>
          <p className="text-xs opacity-60 mt-0.5">Paste a video URL above to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-salty-border overflow-hidden">
            <div className="grid grid-cols-[1fr_110px_90px_90px_32px] bg-salty-bg/60 border-b border-salty-border px-4 py-2 gap-3">
              {['Video', 'Views', 'Likes', 'Comments', ''].map((h, i) => (
                <span key={i} className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted text-right first:text-left">{h}</span>
              ))}
            </div>
            {tracked.map((v, i) => (
              <div
                key={v.video_id}
                className={`grid grid-cols-[1fr_110px_90px_90px_32px] items-center px-4 py-3 gap-3 ${i < tracked.length - 1 ? 'border-b border-salty-border/50' : ''} hover:bg-salty-purple/[0.02] group`}
              >
                <div className="min-w-0">
                  <a
                    href={`https://youtube.com/watch?v=${v.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-salty-text-main hover:text-red-400 transition-colors flex items-center gap-1.5"
                  >
                    <Youtube className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    <span className="truncate">{v.title}</span>
                  </a>
                  {v.published_at && (
                    <p className="text-[10px] text-salty-text-muted mt-0.5">{fmtDate(v.published_at)}</p>
                  )}
                </div>
                <p className="text-sm font-bold text-salty-text-main tabular-nums text-right">{fmt(v.views)}</p>
                <p className="text-sm font-semibold text-salty-text-main tabular-nums text-right">
                  {v.likes > 0 ? fmt(v.likes) : <span className="text-salty-text-muted/50 text-xs">Hidden</span>}
                </p>
                <p className="text-sm font-semibold text-salty-text-main tabular-nums text-right">{fmt(v.comments)}</p>
                <button
                  onClick={() => handleRemove(v.video_id)}
                  className="w-6 h-6 flex items-center justify-center rounded text-salty-text-muted/30 hover:text-rose-500 hover:bg-rose-500/10 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {tracked.length > 1 && (
            <div className="h-52">
              <p className="text-[10px] uppercase tracking-widest font-bold text-salty-text-muted mb-2">Campaign Videos — Views Comparison</p>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="title" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => fmt(v)} width={45} />
                  <Tooltip
                    contentStyle={{ background: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: 8, fontSize: 12, color: 'var(--salty-text-main)' }} labelStyle={{ color: 'var(--salty-text-muted)', fontWeight: 700, marginBottom: 4 }}
                    formatter={(v) => [fmt(v)]}
                  />
                  <Bar dataKey="views" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
